import { getLogger } from '@williamhill/rosi-ui-shared/src/lib/logger-plus';

import { UpdateQueryFn } from 'apollo-client/core/watchQueryOptions';
import { OperationVariables } from 'apollo-client/core/types';
import { SubResponse } from '@williamhill/rosi-ui-shared/src/apollo/types';
import { OnSubscriptionDataOptions } from 'react-apollo';

const logger = getLogger('sub-dedup');

const offsets: Record<string, number> = {};

type Args1<sd extends SubResponse> = (options: OnSubscriptionDataOptions<sd>) => any;
type Args2<sd extends SubResponse> = UpdateQueryFn<any, OperationVariables, sd>;

/**
 * This overload is for use with `useSubscription`'s `onSubscriptionData` callback
 *
 * @param cb Your `onSubscriptionData` callback
 * @returns The callback you give to `useSubscription`
 */
export function withSubDedup<sd extends SubResponse>(
  cb: (options: OnSubscriptionDataOptions<sd>) => any
): (options: OnSubscriptionDataOptions<sd>) => any;

/**
 * This overload is for use with `subscribeToMore`'s `updateQuery` callback
 *
 * @param cb Your `updateQuery` callback
 * @returns The callback you give to `subscribeToMore`
 */
export function withSubDedup<sd extends SubResponse>(
  cb: UpdateQueryFn<any, OperationVariables, sd>
): UpdateQueryFn<any, OperationVariables, sd>;

export function withSubDedup<sd extends SubResponse>(cb: Args1<sd> | Args2<sd>): Args1<sd> | Args2<sd> {
  return Object.assign((...args: any[]) => {
    // Regardless of whether the callback is for use in subscribeToMore or useSubscription, the message
    // that we need to inspect is always in 'subscriptionData.data.sub' of the last argument
    const { data } = args[args.length - 1].subscriptionData; // eslint-disable-line prefer-rest-params

    // If there's an error (e.g., malformed query) then 'data' isn't present and we need to
    // let the wrapped callback deal with it
    if (data !== undefined && data !== null) {
      if (isPreviouslySeen(data)) {
        return;
      } else {
        // This log statement is mined to create reports. Do not remove it.
        logger.info(data.sub, 'Received subscription message');
      }
    }

    return cb.apply(null, args); // eslint-disable-line prefer-spread
  }, cb);
}

const isPreviouslySeen = (data: SubResponse): boolean => {
  if (data.sub?.metadata?.message) {
    const { topic, offset: theirOffset } = data.sub.metadata.message;

    if (topic === undefined || theirOffset === undefined) {
      logger.error('The topic & offset fields of metadata.message must be included for message dedup to work');
    } else {
      const ourOffset = offsets[topic] ?? -1;
      if (ourOffset >= theirOffset) {
        logger.warn({ topic, ourOffset, theirOffset }, 'skipping previously seen message');
        return true;
      }

      offsets[topic] = theirOffset;
    }
  } else {
    logger.error(
      'subscription fields must be renamed to sub and the topic & offset fields ' +
        'of metadata.message must be included for message dedup to work'
    );
  }

  return false;
};
